import React from 'react';
import { Calendar, Clock, ArrowUpRight } from 'lucide-react';
import { isBytesTag, isUndecoded } from './dbCellTags';
import { cellToText, formatByteLen } from './dbCellText';

/**
 * How the DB grid draws things: a cell's contents, a column's type label, and
 * the two affordances that sit inside cells.
 *
 * Presentation only — nothing here reads or writes grid state, which is what
 * lets the grids be tested and reasoned about without the 3,000-line tab
 * component around them.
 */

export const EmptyState = ({ children }) => <div className="dbm-empty">{children}</div>;

// How a cell looks in the grid. The two tagged shapes render as summaries —
// a blob's size rather than a wall of base64, and the column type an
// undecodable value choked on — which is exactly why isOpaqueCell refuses to
// let either be edited: what is on screen is not the value.
// Everything else goes through cellToText, so a JSON/JSONB column shows its
// contents instead of [object Object].
export function renderCellValue(v) {
  if (v === null || v === undefined) return <span className="dbm-null">NULL</span>;
  if (isBytesTag(v)) {
    return <span className="dbm-cell-tag" title={`Binary value, ${v.len} bytes`}>{formatByteLen(v.len)}</span>;
  }
  if (isUndecoded(v)) {
    return (
      <span
        className="dbm-cell-tag dbm-cell-tag--undecoded"
        title={`This ${v.__undecoded} value could not be decoded. The cell is read-only, and generated WHERE clauses match it as IS NOT NULL rather than by value.`}
      >
        {v.__undecoded}
      </span>
    );
  }
  return cellToText(v);
}

// Column-header type label: short enough to sit next to the column name.
// Text-like types are all noisy in their own way (varchar(255), character
// varying, nvarchar(max)…) and the length is rarely what anyone's scanning
// for, so they all collapse to the same "text" label. Numeric types keep
// their precision/scale since that's actually meaningful (numeric(10,2) is a
// different column than numeric(10,0)); everything else drops parenthetical
// noise like MySQL's legacy int(11) display width.
const TEXT_COLUMN_TYPE_RE = /^(var\s?char2?|n?var\s?char|character varying|character|n?char|text|tinytext|mediumtext|longtext|clob|string|citext)\b/i;
const PRECISION_COLUMN_TYPE_RE = /^(decimal|numeric|float|double( precision)?|real)\b/i;
export function shortColumnType(rawType) {
  if (!rawType) return '';
  const raw = String(rawType).trim();
  if (!raw) return '';
  const lower = raw.toLowerCase();
  if (TEXT_COLUMN_TYPE_RE.test(lower)) return 'text';
  // postgres spells these out in full; "with time zone" collapses to the
  // same short name postgres itself accepts (timestamptz/timetz).
  const tzStripped = raw
    .replace(/\s+without\s+time\s+zone$/i, '')
    .replace(/\s+with\s+time\s+zone$/i, 'tz');
  const base = tzStripped.replace(/\s+(unsigned|zerofill)\b/gi, '').trim();
  if (PRECISION_COLUMN_TYPE_RE.test(lower)) return base.toLowerCase();
  return base.replace(/\(.*\)/, '').trim().toLowerCase();
}

// Maps a raw column type to the date-picker kind it should edit with, or null
// for non-temporal columns. Covers the ANSI names plus engine-specific
// spellings — postgres "timestamp without time zone" / "time with time zone",
// MySQL datetime, SQL Server datetime2 / smalldatetime / datetimeoffset.
// Order matters: the timestamp/datetime family is checked before the bare
// date/time prefixes so "timestamp" isn't mis-caught as "time".
export function dateColumnKind(rawType) {
  if (!rawType) return null;
  const t = String(rawType).trim().toLowerCase();
  if (/^(timestamp|datetime|smalldatetime)/.test(t)) return 'datetime';
  if (/^date\b/.test(t)) return 'date';
  if (/^time(tz)?\b/.test(t) || /^time with(out)? time zone/.test(t)) return 'time';
  return null;
}

// Calendar/clock affordance shown in temporal cells. Clicking it opens the
// custom picker anchored to the whole cell (not the button), so the popover
// lines up under the column. mousedown is swallowed so it neither selects the
// cell nor blurs an active editor before the click lands.
export function DateEditButton({ kind, onOpen }) {
  const Icon = kind === 'time' ? Clock : Calendar;
  return (
    <button
      type="button"
      className="dbm-date-btn"
      title="Pick value"
      tabIndex={-1}
      onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
      onClick={(e) => {
        e.stopPropagation();
        onOpen(e.currentTarget.closest('td').getBoundingClientRect());
      }}
    >
      <Icon size={13} />
    </button>
  );
}

// Trailing affordance on a foreign-key cell: opens the referenced table
// narrowed to the row this value points at. Same swallowed mousedown as
// DateEditButton so clicking it neither moves the cell selection nor blurs an
// open editor before the click lands.
export function FkNavigateButton({ title, onNavigate }) {
  return (
    <button
      type="button"
      className="dbm-fk-btn"
      title={title}
      tabIndex={-1}
      onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
      onClick={(e) => {
        e.stopPropagation();
        onNavigate();
      }}
    >
      <ArrowUpRight size={13} />
    </button>
  );
}
